import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import type * as common from '../common'

import type { Item } from './item'
import type { Notice } from './notice'

/**
 * A numerical measurement of some sort
 */
export interface Metric extends Item {
  /**
   * Represents the current measurement of this {@link Metric}
   *
   * @see {@link Metric.Value}
   */
  // NOTE: intentionally optional so that we are forced to handle it
  value?: Metric.Value
  /**
   * Indicates how the current {@link value} compares to previous measurements
   * of this {@link Metric}
   *
   * @see {@link Metric.Change}
   */
  // NOTE: intentionally optional so that we are forced to handle it
  change?: Metric.Change
  /**
   * A {@link Notice} about this metric
   */
  // NOTE: intentionally optional so that we are forced to handle it
  notice?: Notice
  /**
   * Generally used to indicate the recency of this {@link Metric}
   *
   * @see {@link Metric.Event}
   */
  event?: Metric.Event
}
export namespace Metric {
  /**
   * Represents the current measurement of a {@link Metric}
   */
  export interface Value {
    // NOTE: intentionally optional so that we are forced to handle it
    /**
     * Main content being represented by this {@link Value}
     *
     * @example
     * { emphasis: '123' }
     * { emphasis: '123MM' }
     * { emphasis: '$1,234,567' }
     * { emphasis: '45%' }
     * { emphasis: 'C+' }
     * { emphasis: 'Medium' }
     * { emphasis: 'Excellent' }
     * { emphasis: '😊' }
     */
    emphasis?: string
    /**
     * Subtle text that is displayed right next to the {@link emphasis}
     *
     * @example
     * // Unit of measurement
     * { emphasis: '123', aside: 'sqft' }
     * { emphasis: '$56MM', aside: 'MTDC' }
     * // Scale
     * { emphasis: '10', aside: '/ 15' }
     * { emphasis: '99', aside: 'out of 100' }
     * // Goal/target
     * { emphasis: '38', aside: 'goal is 46+' }
     * { emphasis: '99', aside: 'aiming for 77' }
     * // Explanation or supporting information
     * { emphasis: 'B-', aside: '81%' }
     * { emphasis: 'Medium', aside: '12 / 16' }
     */
    aside?: string
    quality?: common.Quality
    tooltip?: string
  }

  /**
   * Indicates how a {@link Metric.Value} compares to its previous value
   */
  export interface Change {
    direction?: Change.Direction
    quality?: common.Quality
    tooltip?: string
  }
  export namespace Change {
    /**
     * Conveys whether a {@link Change} has
     * {@link Direction.Increase increased},
     * {@link Direction.Decrease decreased}, or
     * {@link Direction.Steady stayed the same}
     */
    export enum Direction {
      Increase = '+',
      Decrease = '-',
      Steady = '=',
    }
    // Needs to be before namespace is defined
    const MY_ENUMS: ReadonlySet<Direction> = new Set(
      Object.values(Direction) as Direction[],
    )
    export namespace Direction {
      export const ALL: readonly Direction[] = Array.from(MY_ENUMS)
      export const isDirection = TypeChecker.Factory.oneOf(MY_ENUMS)
      export const assertIsDirection: TypeAsserter<Direction> =
        TypeAsserter.Factory.from(isDirection)
    }
  }

  /**
   * Represents something that happened that relates to a {@link Metric}
   *
   * @example
   * { name: 'Scored', time: 'yesterday' }
   * { name: 'Calculated', time: 'two weeks ago' }
   * { name: 'Assessed', time: 'January 1, 2000' }
   * { name: 'Last Updated', time: '4:15 PM' }
   */
  export interface Event extends common.Named.Optionally {
    // NOTE: intentionally optional so that we are forced to handle it
    time?: string
  }

  /**
   * Often there are multiple related {@link Metric} that should be
   * displayed together. This can be used to represent such a group of
   * {@link Metric}
   */
  export interface Group
    extends common.Named.Optionally,
      common.Described.Optionally {
    /** @default [] */
    // NOTE: intentionally optional so that we are forced to handle it
    values?: readonly Metric[]
  }
  export namespace Group {
    /**
     * Simply a list of {@link Metric.Group}
     *
     * Useful when multiple {@link Metric.Group} are to do be displayed
     * together in one {@link Content}
     */
    export type Series = readonly Group[]
  }
}
